/**
 * Bodies from HORIZONS tables (Sprint 3, S3-06 / D7): Titan relative to Saturn, Arrokoth relative to the Sun. Each
 * coverage window is one decimated table segment (format v2), interpolated with cubic Hermite like trajectories.
 * Outside the windows the body has no position: the scene hides it (it only matters while a mission is near).
 */
import type { EphemerisFile } from './data';
import { findInterval, hermiteAt } from './hermite';
import { type DecodedSegment, decodeSegment } from './trajectoryCodec';
import type { Vec3 } from './vec3';

/** A decoded table (also what the loader's worker hands over, S3-03). */
export interface DecodedTable {
  body: string;
  center: string;
  segments: DecodedSegment[];
}

export class BodyTable {
  readonly body: string;
  /** The body the positions are relative to ('sun' or the parent planet). */
  readonly center: string;
  private readonly segments: DecodedSegment[];
  private readonly hints: number[];
  private last = 0;

  constructor(data: DecodedTable) {
    this.body = data.body;
    this.center = data.center;
    this.segments = [...data.segments].sort((a, b) => a.t[0]! - b.t[0]!);
    this.hints = this.segments.map(() => 0);
  }

  /** Decodes a table file (format v2). */
  static decode(file: EphemerisFile): DecodedTable {
    if (file.format !== 2) throw new Error(`${file.body}: unsupported table format ${String(file.format)}`);
    return { body: file.body, center: file.center, segments: file.segments.map(decodeSegment) };
  }

  static fromFile(file: EphemerisFile): BodyTable {
    return new BodyTable(BodyTable.decode(file));
  }

  /** Coverage windows [start, end], days since J2000 TDB. */
  windows(): [number, number][] {
    return this.segments.map((s) => [s.t[0]!, s.t[s.t.length - 1]!]);
  }

  /** Whether the table covers `t`. */
  covers(t: number): boolean {
    return this.segmentAt(t) >= 0;
  }

  private segmentAt(t: number): number {
    const s = this.segments;
    const last = s[this.last];
    if (last && t >= last.t[0]! && t <= last.t[last.t.length - 1]!) return this.last;
    for (let i = 0; i < s.length; i++) {
      const seg = s[i]!;
      if (t >= seg.t[0]! && t <= seg.t[seg.t.length - 1]!) return (this.last = i);
    }
    return -1;
  }

  /**
   * Position (and velocity, km/day) relative to `center` at `t` (days since J2000 TDB). Returns false outside the
   * coverage windows (`out` unchanged).
   */
  at(t: number, out: Vec3, vel?: Vec3): boolean {
    const i = this.segmentAt(t);
    if (i < 0) return false;
    const seg = this.segments[i]!;
    const h = findInterval(seg.t, t, this.hints[i]!);
    this.hints[i] = h;
    hermiteAt(seg.t, seg.s, h, t, out, vel);
    return true;
  }
}
